import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { BUCKETS, BUCKET_LABELS, money } from '../statementFormat';

const Figure = ({ label, value, detail }) => (
  <Paper variant="outlined" sx={{ p: 2, minWidth: 180, flex: '1 1 180px' }}>
    <Typography variant="overline" color="textSecondary">{label}</Typography>
    <Typography variant="h5">{value}</Typography>
    {detail && <Typography variant="caption" color="textSecondary">{detail}</Typography>}
  </Paper>
);

Figure.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  detail: PropTypes.string,
};

// Warnings about what the totals leave out, so a short statement is never mistaken for a full one.
const Caveats = ({ statement }) => {
  const { summary, pending, failed, otherCurrencyCount, isTruncated } = statement;
  const { matched, payments } = summary.commissionCoverage;
  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {isTruncated && (
        <Alert severity="warning">
          Adyen returned more transfers than one statement can hold, so later transfers are missing. Use a shorter date range.
        </Alert>
      )}
      {matched < payments && (
        <Alert severity="info">
          OrderBuddy commission was found for {matched} of {payments} payments. Commission is recorded from Adyen
          webhooks, so earlier payments have none.
        </Alert>
      )}
      {(pending.count > 0 || failed.count > 0) && (
        <Alert severity="info">
          Not in the totals: {pending.count} pending ({money(pending.amount)}) and {failed.count} failed or returned transfers.
        </Alert>
      )}
      {otherCurrencyCount > 0 && (
        <Alert severity="warning">
          {otherCurrencyCount} transfers are not in {statement.currency} and are left out of the totals.
        </Alert>
      )}
    </Stack>
  );
};

Caveats.propTypes = {
  statement: PropTypes.object.isRequired,
};

const StatementSummary = ({ statement }) => {
  const { summary, balance, otherTypes } = statement;
  return (
    <>
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <Figure
          label="Customers paid"
          value={money(summary.grossPayments)}
          detail={`Payments ${money(summary.payments.amount)} + commission ${money(summary.commission)}`}
        />
        <Figure label="OrderBuddy commission" value={money(summary.commission)} />
        <Figure label="Net to merchant" value={money(summary.net)} detail="All settled movements in the range" />
        {balance && (
          <Figure
            label="Balance now"
            value={money(balance.available)}
            detail={`Available · ${money(balance.pending)} pending`}
          />
        )}
      </Stack>

      <Caveats statement={statement} />

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table size="small" aria-label="Adyen statement summary">
          <TableHead>
            <TableRow>
              <TableCell>Movement</TableCell>
              <TableCell align="right">Transfers</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {BUCKETS.map((bucket) => (
              <TableRow key={bucket}>
                <TableCell>{BUCKET_LABELS[bucket]}</TableCell>
                <TableCell align="right">{summary[bucket].count}</TableCell>
                <TableCell align="right">{money(summary[bucket].amount)}</TableCell>
              </TableRow>
            ))}
            {otherTypes.map((row) => (
              <TableRow key={row.type}>
                <TableCell sx={{ pl: 4 }}>
                  <Typography variant="body2" color="textSecondary">Other: {row.type}</Typography>
                </TableCell>
                <TableCell align="right">{row.count}</TableCell>
                <TableCell align="right">{money(row.amount)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell><strong>Net</strong></TableCell>
              <TableCell />
              <TableCell align="right"><strong>{money(summary.net)}</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

StatementSummary.propTypes = {
  statement: PropTypes.shape({
    summary: PropTypes.object.isRequired,
    balance: PropTypes.object,
    otherTypes: PropTypes.array.isRequired,
  }).isRequired,
};

export default StatementSummary;
